import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  tooltipWrapper: {
    display: "inline-block",
    position: "relative",
    lineHeight: "normal",
    cursor: "pointer",
  },
  tooltipWrapperFullWidth: {
    width: "100%",
  },
  tooltip: {
    position: "fixed", // Changed from absolute to fixed for portal
    zIndex: 9999,
    background: "#ffffff",
    color: "#0C0E29",
    padding: "8px 8px",
    borderRadius: 8,
    fontSize: 14,
    whiteSpace: "nowrap",
    boxShadow: "0 2px 4px rgba(12, 14, 41, 0.1), 0 4px 8px rgba(12, 14, 41, 0.1)",
    opacity: 0,
    pointerEvents: "none",
    transition: "opacity 0.2s",
    "&.visible": {
      opacity: 1,
      pointerEvents: "auto",
    },
  },
}));

const Tooltip = ({ children, content, placement = "top", fullwidth = false }) => {
  const classes = useStyles();
  const [visible, setVisible] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const timeoutRef = useRef();
  const wrapperRef = useRef();
  
  const calculatePosition = () => {
    if (!wrapperRef.current) return;
    
    const rect = wrapperRef.current.getBoundingClientRect();
    // Don't add scroll offsets for fixed positioning - getBoundingClientRect already gives viewport coordinates
    
    let top, left;
    
    if (placement === "top") {
      top = rect.top - 8; // 8px margin above
      left = rect.left + rect.width / 2;
    } else {
      top = rect.bottom + 8; // 8px margin below
      left = rect.left + rect.width / 2;
    }
    
    setTooltipPosition({ 
      top: top + "px", 
      left: left + "px",
      transform: "translateX(-50%)" + (placement === "top" ? " translateY(-100%)" : "")
    });
  };

  const showTooltip = () => {
    // Check if this tooltip is inside a dialog
    const isInsideDialog = wrapperRef.current?.closest('[role="dialog"], .MuiDialog-root, .modal, [data-testid*="dialog"]');

    // Don't show tooltip if a dialog is open AND this tooltip is not inside that dialog
    if (dialogOpen && !isInsideDialog) return;

    calculatePosition();
    timeoutRef.current = setTimeout(() => setVisible(true), 100);
  };

  const hideTooltip = () => {
    clearTimeout(timeoutRef.current);
    setVisible(false);
  };

  // Effect to handle clicks outside the tooltip and when dialogs/modals open
  useEffect(() => {
    const checkForDialogs = () => {
      const dialogs = document.querySelectorAll('[role="dialog"], .MuiDialog-root, .modal, [data-testid*="dialog"]');
      const hasDialog = dialogs.length > 0;

      // Check if this tooltip is inside a dialog
      const isInsideDialog = wrapperRef.current?.closest('[role="dialog"], .MuiDialog-root, .modal, [data-testid*="dialog"]');

      if (hasDialog && !dialogOpen) {
        setDialogOpen(true);
        // Only hide tooltip if it's not inside the dialog
        if (!isInsideDialog) {
          hideTooltip();
        }
      } else if (!hasDialog && dialogOpen) {
        setDialogOpen(false);
      }
    };

    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        hideTooltip();
      }
    };

    const handleScroll = () => {
      if (visible) {
        calculatePosition();
      }
    };

    // Check for dialogs immediately
    checkForDialogs();

    // Add event listeners
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleScroll);
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        hideTooltip();
      }
    });

    // Use a mutation observer to detect when dialogs are added/removed from the DOM
    const observer = new MutationObserver(checkForDialogs);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleScroll);
      document.removeEventListener("keydown", hideTooltip);
      observer.disconnect();
    };
  }, [dialogOpen, visible]); // Include visible in dependencies

  const handleClick = () => {
    hideTooltip();
  };

  const wrapperClass = fullwidth ? `${classes.tooltipWrapper} ${classes.tooltipWrapperFullWidth}` : classes.tooltipWrapper;

  // Check if we're inside a table - if so, use portal to render tooltip outside
  const isInTable = wrapperRef.current?.closest('table');
  
  const tooltipElement = (
    <span 
      className={`${classes.tooltip} ${visible ? "visible" : ""}`} 
      role="tooltip"
      style={{
        ...tooltipPosition,
        whiteSpace: typeof content === 'string' && content.length > 50 ? 'normal' : 'nowrap',
        maxWidth: typeof content === 'string' && content.length > 50 ? '300px' : 'none'
      }}
    >
      {content}
    </span>
  );

  return (
    <>
      <span 
        className={wrapperClass} 
        onMouseEnter={showTooltip} 
        onMouseLeave={hideTooltip} 
        onFocus={showTooltip} 
        onBlur={hideTooltip} 
        onClick={handleClick} 
        ref={wrapperRef} 
        tabIndex={0}
      >
        {children}
      </span>
      {visible && isInTable ? createPortal(tooltipElement, document.body) : visible && tooltipElement}
    </>
  );
};

export default Tooltip;
